import { useEffect, useState } from 'react';
import { PRODUCT_TYPE } from '../config/api';
import useStore from '../store/useStore';
import { getProductIcon, getProductTypeName } from '../utils/productPresentation';
import { getBackendRewardAmount, getProductPromoPercent } from '../utils/productUtils';
import { formatRemainingAsHms, getRemainingToTodayEndMs } from '../utils/serverClock';

// 格式化数字显示 - 使用千分位格式
const formatNumber = (num) => {
  // 确保 num 是数字
  const value = Number(num);
  if (isNaN(value)) return '0';
  return value.toLocaleString('en-US');
};

const accents = {
  coins: {
    shell:
      'border-[#e4d8ff] bg-[linear-gradient(180deg,rgba(255,255,255,0.94),rgba(255,245,224,0.9))] shadow-[0_20px_40px_rgba(111,123,255,0.1)] dark:border-gold/35 dark:bg-[linear-gradient(180deg,rgba(255,222,145,0.18),rgba(255,196,74,0.05))]',
    iconGlow: 'shadow-[0_0_45px_rgba(247,196,90,0.28)]',
    timer:
      'border-[#ffd99f] bg-[linear-gradient(180deg,#fff3d0_0%,#ffdca0_100%)] text-[#764700] dark:border-[#6c3d00] dark:bg-[linear-gradient(180deg,rgba(255,205,107,0.18),rgba(168,84,0,0.26))] dark:text-[#ffd99f]',
    bonusSticker:
      'border-[#fff4ca]/65 bg-[linear-gradient(180deg,#fff6cf_0%,#ffd867_42%,#ff9d2f_100%)] shadow-[0_16px_32px_rgba(255,174,44,0.32)]',
    bonusText: 'text-[#422100]',
    button:
      'bg-[linear-gradient(135deg,#ffd46c,#f2a32d)] text-[#2b1300] shadow-glow',
  },
  jewels: {
    shell:
      'border-[#d8ebff] bg-[linear-gradient(180deg,rgba(255,255,255,0.94),rgba(234,246,255,0.94))] shadow-[0_20px_40px_rgba(111,123,255,0.1)] dark:border-sapphire/35 dark:bg-[linear-gradient(180deg,rgba(115,219,255,0.19),rgba(87,96,255,0.05))]',
    iconGlow: 'shadow-[0_0_45px_rgba(80,196,255,0.28)]',
    timer:
      'border-[#bde1ff] bg-[linear-gradient(180deg,#eef9ff_0%,#d7efff_100%)] text-[#1e63b1] dark:border-[#1e4ba8] dark:bg-[linear-gradient(180deg,rgba(126,224,255,0.16),rgba(40,86,200,0.26))] dark:text-[#bfeeff]',
    bonusSticker:
      'border-[#d8fbff]/55 bg-[linear-gradient(180deg,#ddffff_0%,#7ee0ff_40%,#4366ff_100%)] shadow-[0_16px_32px_rgba(64,149,255,0.35)]',
    bonusText: 'text-[#03182d]',
    button:
      'bg-[linear-gradient(135deg,#74dfff,#3978ff)] text-[#03121f] shadow-sapphire',
  },
};

const ProductCard = ({ product, type }) => {
  const {
    isAuthenticated,
    setLoginModalOpen,
    setPaymentModalOpen,
    setSelectedProduct,
    user,
  } = useStore();

  const getCountdownText = (currentUser) => {
    const remainingMs = getRemainingToTodayEndMs(currentUser);
    if (remainingMs === null) {
      return '--:--:--';
    }
    return formatRemainingAsHms(remainingMs);
  };

  const [countdown, setCountdown] = useState(() => {
    return getCountdownText(user);
  });

  useEffect(() => {
    const updateCountdown = () => {
      setCountdown(getCountdownText(useStore.getState().user));
    };

    updateCountdown();
    const id = window.setInterval(() => {
      updateCountdown();
    }, 1000);
    return () => window.clearInterval(id);
  }, [user?.todayEnd, user?.today_end, user?.serverTimeOffsetMs]);

  const handleCardClick = () => {
    if (!isAuthenticated) {
      setLoginModalOpen(true);
    } else {
      setSelectedProduct(product);
      setPaymentModalOpen(true);
    }
  };

  // 判断是否为基准商品（不参与活动折扣）
  const isBaseProduct = product.productType === PRODUCT_TYPE.BASE_COIN ||
                        product.productType === PRODUCT_TYPE.BASE_GEM;

  // 除基准商品（1101/1201）外，其余都视为促销商品
  const isPromoProduct = !isBaseProduct;

  // 展示值：划线=baseValue，现值=serverReward（Rewards 求和）
  const baseValueRaw = Number(product.baseValue);
  const serverRewardRaw = Number(getBackendRewardAmount(product));
  const baseValue = Number.isFinite(baseValueRaw) ? baseValueRaw : 0;
  const serverReward = Number.isFinite(serverRewardRaw) ? serverRewardRaw : 0;

  const discountPercent = getProductPromoPercent(product);

  // 是否有优惠显示
  const hasDiscount = isPromoProduct && discountPercent > 0;

  // 获取商品类型
  const productTypeName = getProductTypeName(product.productType);
  const productIcon = getProductIcon(product.productType, product.price);

  // 是否为Hot/Best商品（基准商品不显示Hot/Best标签）
  const isHot = !isBaseProduct && (product.isHot || (type === 'hot'));
  const isBest = !isBaseProduct && product.isBest;

  const discountSizeClass = hasDiscount
    ? discountPercent >= 100
      ? "text-[1.1rem] sm:text-[1.45rem]"
      : "text-[1.25rem] sm:text-[1.75rem]"
    : "";
  const formattedReward = formatNumber(serverReward);
  const amountTextClass = 'mt-2 flex min-h-[3.9rem] w-full min-w-0 flex-wrap items-center justify-center gap-x-2 gap-y-0.5 overflow-hidden text-center font-display text-[clamp(1rem,4.2vw,1.25rem)] font-black uppercase leading-tight tracking-normal text-[#20163c] dark:text-white sm:min-h-[4.5rem] sm:text-[clamp(1rem,1.6vw,1.55rem)]';

  const currency = product.productType === PRODUCT_TYPE.NEW_STORE_GEM || product.productType === PRODUCT_TYPE.BASE_GEM ? 'jewels' : 'coins';
  const accent = accents[currency];

  return (
    <article
      onClick={handleCardClick}
      className={`group relative h-full min-w-0 cursor-pointer overflow-hidden rounded-[1.35rem] border-[2px] p-3.5 text-[#1c1633] transition duration-300 hover:-translate-y-1 dark:text-white ${accent.shell} sm:rounded-[1.9rem] sm:p-5`}
    >
      {/* Hover overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.18),transparent_60%)] opacity-0 transition duration-300 group-hover:opacity-100" />
      {/* Top gloss */}
      <div className="absolute inset-x-4 top-0 h-5 rounded-b-full bg-white/45 blur-md dark:bg-white/10 sm:h-6" />

      <div className="relative z-10 flex h-full min-w-0 flex-col">
        {/* Tag row + Bonus sticker */}
        <div className="flex h-[3.9rem] items-start justify-between gap-2.5 sm:h-[4.2rem] sm:gap-3">
          <div className="h-[2rem] min-w-0 sm:h-[2.2rem]">
            {isBest && (
              <span className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-purple-500 to-pink-500 px-3 py-1 text-[10px] font-black uppercase leading-none tracking-[0.22em] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.38)] sm:tracking-[0.28em]">
                BEST
              </span>
            )}
            {isHot && !isBest && (
              <span className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 px-3 py-1 text-[10px] font-black uppercase leading-none tracking-[0.22em] text-[#2b1300] shadow-[inset_0_0_0_1px_rgba(255,240,179,0.72)] sm:tracking-[0.28em]">
                HOT
              </span>
            )}
          </div>
          {hasDiscount ? (
            <div className={`-rotate-6 flex h-14 w-[5.4rem] shrink-0 items-center justify-center rounded-[0.95rem] border-[2px] px-2 py-2 text-center sm:h-[4.2rem] sm:w-[6rem] sm:rounded-[1.1rem] sm:px-3 sm:py-3 ${accent.bonusSticker}`}>
              <p className={`font-display font-black leading-none ${discountSizeClass} ${accent.bonusText}`}>
                +{discountPercent}%
              </p>
            </div>
          ) : (
            <div className="h-14 w-[5.4rem] shrink-0 sm:h-[4.2rem] sm:w-[6rem]" aria-hidden="true" />
          )}
        </div>

        {/* Countdown timer - 促销显示；基准商品保留同高度占位，保证卡片内容对齐 */}
        {isPromoProduct ? (
          <div className={`mt-2.5 h-[3.35rem] rounded-[0.9rem] border px-2.5 py-1.5 sm:mt-3 sm:h-[3.5rem] sm:rounded-[0.95rem] sm:px-2.5 sm:py-2 ${accent.timer}`}>
            <p className="text-[8px] font-black uppercase tracking-[0.2em] opacity-75 sm:text-[9px] sm:tracking-[0.24em]">
              Promo Ends In
            </p>
            <p className="mt-1 font-display text-base font-black uppercase leading-none sm:text-lg">
              {countdown}
            </p>
          </div>
        ) : (
          <div className="mt-2.5 h-[3.35rem] sm:mt-3 sm:h-[3.5rem]" aria-hidden="true" />
        )}

        {/* Icon container */}
        <div className={`mt-4 rounded-[1.2rem] border border-[#e5e6fb] bg-white/65 p-3 dark:border-white/10 dark:bg-black/25 sm:mt-4 sm:rounded-[1.6rem] sm:p-4 ${accent.iconGlow}`}>
          <img
            src={productIcon}
            alt={`${productTypeName} bundle`}
            className="mx-auto h-28 w-28 object-contain drop-shadow-[0_10px_18px_rgba(0,0,0,0.16)] sm:h-32 sm:w-32"
          />
        </div>

        {/* Amount display */}
        <div className="mt-[1.15rem] min-h-[5.9rem] min-w-0 sm:mt-5 sm:min-h-[6.25rem]">
          {isPromoProduct && hasDiscount ? (
            <>
              {/* 促销商品且有优惠：显示划线价格和最终价格 */}
              <p className="text-center text-xs font-semibold uppercase tracking-[0.16em] text-[#6f6787] line-through dark:text-white/45 sm:text-sm sm:tracking-[0.2em]">
                was {formatNumber(baseValue)}
              </p>
              <p className={amountTextClass}>
                <span>{formattedReward}</span>
                <span className="shrink-0">{productTypeName}</span>
              </p>
            </>
          ) : (
            /* 基准商品或无优惠的促销商品：预留划线位置，只显示最终价格 */
            <>
              <p className="text-center text-xs font-semibold uppercase tracking-[0.16em] text-transparent line-through sm:text-sm sm:tracking-[0.2em]">
                placeholder
              </p>
              <p className={amountTextClass}>
                <span>{formattedReward}</span>
                <span className="shrink-0">{productTypeName}</span>
              </p>
            </>
          )}
        </div>

        {/* Purchase button */}
        <div className="mt-auto pt-5 sm:pt-6">
          <button
            type="button"
            className={`w-full rounded-full px-4 py-3 font-display text-sm font-black uppercase leading-tight tracking-[0.2em] transition hover:scale-[1.02] hover:brightness-105 sm:px-4 sm:text-sm sm:tracking-[0.28em] ${accent.button}`}
          >
            ${product.price} USD
          </button>
        </div>
      </div>
    </article>
  );
};

export default ProductCard;
